import { useEffect, useRef } from "react";
import { Landmark, Trees, Building2, Lock } from "lucide-react";
import { useLang } from "../LanguageContext";

const IMAGE1 = "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/uexsopr8_torreamat-gal3.jpg";
const IMAGE2 = "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/rlx4orj7_Interior%20Galeria.jpeg";

const icons = [Landmark, Trees, Building2, Lock];

export default function EspacioSection() {
  const { t } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in-visible")),
      { threshold: 0.12 }
    );
    const els = ref.current?.querySelectorAll(".fade-in-item");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const features = [
    { key: "feature1", label: t.espacio.feature1, desc: t.espacio.feature1_desc },
    { key: "feature2", label: t.espacio.feature2, desc: t.espacio.feature2_desc },
    { key: "feature3", label: t.espacio.feature3, desc: t.espacio.feature3_desc },
    { key: "feature4", label: t.espacio.feature4, desc: t.espacio.feature4_desc },
  ];

  return (
    <section
      id="espacio"
      ref={ref}
      data-testid="espacio-section"
      className="bg-[#F5F0E8] py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Caption */}
        <p
          className="fade-in-item text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-4"
          style={{ fontFamily: "Lato, sans-serif" }}
        >
          {t.espacio.caption}
        </p>
        {/* Gold rule */}
        <div className="fade-in-item w-12 h-px bg-[#C8A96E] mb-10" />

        {/* Main grid: text left, image right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start mb-20">
          {/* Text column */}
          <div className="fade-in-item">
            <h2
              className="text-4xl md:text-5xl font-light text-[#1A1A18] leading-tight mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t.espacio.heading}
            </h2>
            <p
              className="text-base text-[#1A1A18]/75 leading-relaxed mb-6"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {t.espacio.p1}
            </p>
            <p
              className="text-base text-[#1A1A18]/75 leading-relaxed mb-6"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {t.espacio.p2}
            </p>
            <p
              className="text-base text-[#1A1A18]/60 leading-relaxed italic"
              style={{ fontFamily: "'Libre Baskerville', serif" }}
            >
              {t.espacio.p3}
            </p>
          </div>

          {/* Image column */}
          <div className="fade-in-item relative">
            <img
              src={IMAGE1}
              alt="Torre Amat garden"
              className="w-full h-[480px] object-cover"
            />
          </div>
        </div>

        {/* Second row: image left, features right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Image */}
          <div className="fade-in-item order-2 lg:order-1">
            <img
              src={IMAGE2}
              alt="Torre Amat entrance"
              className="w-full h-[400px] object-cover"
            />
          </div>

          {/* Features */}
          <div className="fade-in-item order-1 lg:order-2 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {features.map(({ key, label, desc }, i) => {
              const Icon = icons[i];
              return (
                <div key={key} data-testid={`feature-${key}`} className="group">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#C8A96E]/40 mb-4 group-hover:border-[#C8A96E] transition-colors duration-300">
                    <Icon size={18} className="text-[#C8A96E]" strokeWidth={1.5} />
                  </div>
                  <h3
                    className="text-base font-medium text-[#1A1A18] mb-2"
                    style={{ fontFamily: "'Libre Baskerville', serif" }}
                  >
                    {label}
                  </h3>
                  <p
                    className="text-sm text-[#1A1A18]/60 leading-relaxed"
                    style={{ fontFamily: "Lato, sans-serif" }}
                  >
                    {desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Download dossiers */}
        <div className="fade-in-item mt-16 pt-12 border-t border-[#C8A96E]/20 flex flex-col sm:flex-row gap-4">
          <a
            data-testid="download-diptic"
            href="https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/rbmb6e4b_D%C3%ADptic%20Torre%20Amat.pdf"
            download="Torre_Amat_Diptic.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#2D4A2D] text-[#F5F0E8] text-xs uppercase tracking-[0.2em] hover:bg-[#C8A96E] hover:text-[#1A1A18] transition-all duration-300"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {t.espacio.download_diptico}
          </a>
          <a
            data-testid="download-plano"
            href="https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/wl2j06u9_Plano%20Torre%20Amat%20mides.pdf"
            download="Torre_Amat_Plano.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#2D4A2D] text-[#F5F0E8] text-xs uppercase tracking-[0.2em] hover:bg-[#C8A96E] hover:text-[#1A1A18] transition-all duration-300"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {t.espacio.download_plano}
          </a>
        </div>
      </div>
    </section>
  );
}
